import { useState, type MouseEvent } from 'react';
import { ChevronLeft, Menu } from 'lucide-react';
import { useTenantLogoSrc } from '../../../features/tenant/hooks/useTenantLogoSrc';
import { useI18n } from '../../../i18n/useI18n';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { LiksBrand } from '../LiksBrand/LiksBrand';
import { LiksIconButton } from '../LiksIconButton/LiksIconButton';
import { LiksChatSessions } from '../LiksChatSessions/LiksChatSessions';
import { LiksSidebarNav } from './LiksSidebarNav';
import { LiksSidebarUserMenu } from './LiksSidebarUserMenu';
import type { LiksSidebarNavItem } from './sidebarNavItems';
import './LiksSidebar.css';

interface LiksSidebarProps {
  items: LiksSidebarNavItem[];
  isDesktop: boolean;
  isExpanded: boolean;
  isOpen: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onClose: () => void;
}

export function LiksSidebar({
  items,
  isDesktop,
  isExpanded,
  isOpen,
  onExpand,
  onCollapse,
  onClose,
}: LiksSidebarProps) {
  const { translate } = useI18n();
  const { keepSidebarOpen } = useUserConfig();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const showLabels = !isDesktop || isExpanded;
  const showTooltips = isDesktop && !isExpanded;
  const isDesktopCollapsed = isDesktop && !isExpanded;
  const tenantLogoSrc = useTenantLogoSrc();

  const handleCloseSidebar = () => {
    setIsUserMenuOpen(false);
    onClose();
  };

  const handleSidebarClick = () => {
    if (isDesktopCollapsed) {
      onExpand();
    }
  };

  const handleNavLinkClick = () => {
    setIsUserMenuOpen(false);
    if (!isDesktop) {
      handleCloseSidebar();
      return;
    }
    if (!keepSidebarOpen) {
      onCollapse();
      return;
    }
    if (!isExpanded) {
      onExpand();
    }
  };

  const handleCollapseClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsUserMenuOpen(false);
    onCollapse();
  };

  const sidebarClassName = [
    'liks-sidebar',
    isDesktop ? 'liks-sidebar--desktop' : 'liks-sidebar--mobile',
    isDesktop && isExpanded && 'liks-sidebar--expanded',
    isDesktop && !isExpanded && 'liks-sidebar--collapsed',
    !isDesktop && isOpen && 'liks-sidebar--open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {!isDesktop && isOpen && (
        <button
          type="button"
          className="liks-sidebar__backdrop"
          aria-label={translate('layout.closeSidebar')}
          onClick={handleCloseSidebar}
        />
      )}

      <aside
        className={sidebarClassName}
        aria-label={translate('layout.mainNavigation')}
        onClick={isDesktopCollapsed ? handleSidebarClick : undefined}
      >
        <div className="liks-sidebar__header">
          <div className="liks-sidebar__brand-group">
            <LiksBrand
              showText={showLabels}
              logoSrc={tenantLogoSrc ?? undefined}
            />
          </div>
          {showLabels &&
            (isDesktop ? (
              <button
                type="button"
                className="liks-sidebar__collapse"
                aria-label={translate('layout.collapseSidebar')}
                onClick={handleCollapseClick}
              >
                <ChevronLeft
                  className="liks-sidebar__icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            ) : (
              <LiksIconButton
                variant="on-dark"
                aria-label={translate('layout.closeMenu')}
                onClick={handleCloseSidebar}
              >
                <Menu
                  className="liks-sidebar__menu-icon"
                  strokeWidth={2}
                  aria-hidden
                />
              </LiksIconButton>
            ))}
        </div>
        <div
          className="liks-sidebar__divider liks-sidebar__divider--header"
          role="separator"
          aria-hidden
        />

        <div className="liks-sidebar__body">
          <LiksSidebarNav
            items={items}
            showLabels={showLabels}
            showTooltips={showTooltips}
            onNavLinkClick={handleNavLinkClick}
          />

          {showLabels && (
            <>
              <div
                className="liks-sidebar__divider"
                role="separator"
                aria-hidden
              />
              <LiksChatSessions onSessionClick={handleNavLinkClick} />
            </>
          )}
        </div>

        <div className="liks-sidebar__footer">
          <LiksSidebarUserMenu
            showLabels={showLabels}
            showTooltips={showTooltips}
            isDesktopCollapsed={isDesktopCollapsed}
            isOpen={isUserMenuOpen}
            onOpenChange={setIsUserMenuOpen}
            onExpand={onExpand}
            onNavLinkClick={handleNavLinkClick}
          />
        </div>
      </aside>
    </>
  );
}
